import axios, {
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import authService from "auth/authService";
import {
  getAppSession,
  shouldRefreshSession,
  setSessionExpiry,
} from "./appSession";
import { refreshSessionApi } from "./authApi";
import { encryptPayload, decryptPayload, EncryptedPayload } from "./cryptoUtil";
import { DECRYPT_FAILED_CODE } from "./errors/parseApiError";

// In production the frontend and backend share an origin via a Firebase Hosting
// "/api/**" rewrite, so this is a relative path — keeps the session cookie
// first-party. Local dev overrides it with REACT_APP_BACKEND_URL (emulator URL).
// const BASE_URL = "http://127.0.0.1:5001/keavalley-gardio/us-central1/usersApp";
const BASE_URL = process.env.REACT_APP_BACKEND_URL ?? "/api";

const apiClient = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
});

type SessionBoundConfig = InternalAxiosRequestConfig & {
  sessionKey?: CryptoKey;
};

// Decrypt with the key the request went out under: the session key rotates every ~2 minutes,
// and a rotation mid-flight would otherwise turn a perfectly good 200 into a failure.
async function keyFor(config?: InternalAxiosRequestConfig): Promise<CryptoKey> {
  const bound = (config as SessionBoundConfig | undefined)?.sessionKey;
  return bound ?? (await getAppSession()).sessionKey;
}

// WebCrypto's DOMException carries an empty message, so a decrypt failure only leaves a trace if
// we write one ourselves. Returns the detail so the thrown error and the log agree.
function logDecryptFailure(
  what: string,
  config: InternalAxiosRequestConfig | undefined,
  cause: unknown,
): string {
  const where = `${(config?.method ?? "?").toUpperCase()} ${config?.url ?? "?"}`;
  const detail = `could not decrypt the ${what} of ${where}`;
  console.error(`apiClient: ${detail}`, cause);
  return detail;
}

// A failed decrypt is not a failed call: the server answered, we just can't read the answer. The
// code is what lets parseApiError say exactly that instead of falling back to the caller's
// "the operation failed" copy, which would invite a retry of something that already happened.
function decryptFailure(
  what: string,
  config: InternalAxiosRequestConfig | undefined,
  cause: unknown,
): Error {
  const err = new Error(logDecryptFailure(what, config, cause), {
    cause,
  }) as Error & { code: string };
  err.code = DECRYPT_FAILED_CODE;
  return err;
}

let refreshInflight: Promise<void> | null = null;

async function refreshIfNeeded(): Promise<void> {
  if (!shouldRefreshSession()) return;
  if (!refreshInflight) {
    refreshInflight = refreshSessionApi()
      .then(({ sessionExpiresAt }) => {
        setSessionExpiry(sessionExpiresAt);
      })
      // Non-fatal: the request still goes out, and a dead session 401s into the
      // response interceptor.
      .catch(() => {})
      .finally(() => {
        refreshInflight = null;
      });
  }
  return refreshInflight;
}

apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    await refreshIfNeeded();

    const idToken = await authService.getToken();
    if (!idToken) throw new Error("No authenticated user");
    config.headers.Authorization = `Bearer ${idToken}`;

    const appSession = await getAppSession();
    config.headers["X-App-Token"] = appSession.token;
    (config as SessionBoundConfig).sessionKey = appSession.sessionKey;

    const method = (config.method ?? "").toUpperCase();
    if (["POST", "PUT", "PATCH"].includes(method) && config.data) {
      config.data = await encryptPayload(config.data, appSession.sessionKey);
      config.headers["X-Encrypted"] = "1";
    }

    return config;
  },
  (error) => Promise.reject(error),
);

apiClient.interceptors.response.use(
  async (response: AxiosResponse) => {
    if (response.headers["x-encrypted"] === "1") {
      try {
        response.data = await decryptPayload(
          response.data as EncryptedPayload,
          await keyFor(response.config),
        );
      } catch (cause) {
        throw decryptFailure("response body", response.config, cause);
      }
    }
    return response;
  },
  async (error: AxiosError) => {
    // Before the decrypt: a 401 is always a dead session, so we are already navigating away and
    // no message will ever be rendered. Decrypting it would only cost a session-key fetch.
    if (error.response?.status === 401) {
      await authService.signOut();
      window.location.replace("/");
      return Promise.reject(error);
    }

    // Error bodies go through the same encryptPayloads res.send wrapper as successful ones, so
    // they need decrypting here too — otherwise every caller sees only axios's generic
    // "Request failed with status code N" instead of the reason the server actually sent.
    // Best effort: on failure leave the raw payload rather than replacing the original error
    // with a decryption one.
    if (error.response?.headers?.["x-encrypted"] === "1") {
      try {
        error.response.data = await decryptPayload(
          error.response.data as EncryptedPayload,
          await keyFor(error.config),
        );
      } catch (cause) {
        // Best effort: keep the raw payload rather than replacing the server's error with
        // ours — but never drop the reason on the floor.
        logDecryptFailure("error body", error.config, cause);
      }
    }

    return Promise.reject(error);
  },
);

export default apiClient;
